import React, { useState } from "react";
import { Box, Typography, Grid, Dialog, DialogContent, DialogTitle } from "@mui/material";

// Import images
import image1 from "../../images/knukclesAct1.jpg";
import image2 from "../../images/CardomonAct2.jpg";
import image3 from "../../images/Sri-Dalada-Maligawa-Act3.jpg";
import image4 from "../../images/yogaAct4.jpg";
import image5 from "../../images/pinnawalaAct5.jpg";

function ThrillingTwo() {
    const [open, setOpen] = useState(false);
    const [modalContent, setModalContent] = useState({
        title: "",
        content: "",
        background: "",
    });

    const cardDetails = [
        {
            image: image1,
            alt: "Knuckles Adventures",
            title: "Knuckles Adventures",
            category: "ADVENTURE",
            content: `The Knuckles Mountain Range, a UNESCO World Heritage Site in Sri Lanka, is a hiker’s paradise offering a blend of natural beauty, biodiversity, and cultural experiences. Named for its resemblance to a clenched fist, the range features a variety of trails catering to all skill levels. Trekkers can explore highlights like Mini World’s End, a stunning cliff with panoramic views of the surrounding valleys, or the challenging Five Peaks Trail, which traverses the range’s highest summits. Trails wind through dense forests, grasslands, waterfalls, and traditional villages such as Meemure, offering a glimpse into the region’s timeless rural life. Along the way, adventurers encounter endemic flora and fauna, making it a haven for nature lovers and birdwatchers. Waterfalls like Sera Ella and caves such as Nitro Caves add a touch of adventure to the serene landscapes. Visitors often camp under starry skies, enjoying the tranquility of this untouched wilderness. The range’s rich biodiversity, cultural heritage, and breathtaking vistas make every trek a memorable journey. Whether you’re seeking adventure, photography opportunities, or a peaceful retreat, the Knuckles trails promise an unforgettable experience.`,
        },
        {
            image: image2,
            alt: "Cardamom Picking",
            title: "Cardamom Picking",
            category: "SPICES",
            content: `Step into the aromatic embrace of lush green hills and immerse yourself in the timeless art of cardamom picking. Wander through the fragrant plantations, handpick the queen of spices straight from its verdant pods, and discover the journey of this exotic treasure from plant to palate. A sensory adventure that blends tradition, nature, and the essence of spice. Begin your journey with a guided walk through the plantation, where the air is filled with the warm, earthy aroma of cardamom. Learn about the delicate art of cultivating and harvesting this spice, as skilled workers share their traditional methods handed down through generations. Finish your visit with a warm cup of cardamom-infused tea or a freshly prepared local delicacy, savoring the spice’s rich and complex taste. This is more than an agricultural tour; it’s a journey through history, culture, and the natural wonders of spice cultivation.`,
        },
        {
            image: image3,
            alt: "Cultural Visits",
            title: "Cultural Visits",
            category: "CULTURE",
            content: `A visit to Kandy, the cultural heart of Sri Lanka, is a captivating journey through history, spirituality, and nature. Nestled amidst the misty hills of the central highlands, this UNESCO World Heritage City is home to the Temple of the Sacred Tooth Relic, one of Buddhism’s most sacred sites. Visitors can stroll along the tranquil Kandy Lake, take in breathtaking city views from the Bahirawakanda Buddha Statue and Kandy View Point, or explore the lush Peradeniya Royal Botanical Gardens, known for its diverse flora. For a deeper cultural experience, the vibrant Kandyan Cultural Show offers traditional dances and drumming, while the bustling Kandy Market Hall is perfect for discovering local crafts and flavors. With its rich traditions, stunning landscapes, and welcoming atmosphere, Kandy offers a blend of relaxation, adventure, and cultural immersion.`,
        },
        {
            image: image4,
            alt: "Yoga Retreat",
            title: "Yoga Retreat",
            category: "WELLNESS",
            content: `Yoga and meditation in the Knuckles Mountain Range offer a peaceful and rejuvenating experience, surrounded by misty peaks, lush forests, and tranquil valleys. The serene environment and fresh air provide the perfect setting for yoga sessions, often held near waterfalls or ancient trees, blending Hatha and Vinyasa practices. Meditation thrives here as well, with the calming sounds of nature and stunning views fostering relaxation, mental clarity, and a deeper connection to the present moment. This tranquil setting is ideal for those seeking inner peace and spiritual rejuvenation.`,
        },
        {
            image: image5,
            alt: "Pinnawala Orphanage",
            title: "Pinnawala Orphanage",
            category: "WILDLIFE",
            content: `The Pinnawala Elephant Orphanage, located near Kandy, Sri Lanka, is a sanctuary for orphaned and abandoned elephants. Established in 1975, it offers visitors a chance to witness elephants being fed, bathed in the nearby Maha Oya River, and interacting with caretakers. The orphanage is also involved in elephant breeding and conservation efforts. Visitors can watch young elephants at the nursery, learn about elephant care, and explore the elephant museum. Pinnawala is a unique opportunity to observe these majestic creatures up close in a protected environment, while also promoting awareness of elephant conservation.`,
        },
    ];

    const handleOpen = (title, content, background) => {
        setModalContent({ title, content, background });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    return (
        <Box sx={{ py: 8, px: 2, backgroundColor: "#f9f9f9" }}>
            <Grid container spacing={4} justifyContent="center">
                {cardDetails.map((card, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box
                            sx={{
                                position: "relative",
                                overflow: "hidden",
                                borderRadius: "12px",
                                "&:hover": {
                                    transform: "scale(1.02)",
                                    transition: "all 0.3s ease-in-out",
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src={card.image}
                                alt={card.alt}
                                sx={{
                                    width: "100%",
                                    height: "250px",
                                    objectFit: "cover",
                                    borderRadius: "12px",
                                }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "86%",
                                    borderRadius: "15px",
                                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                                    zIndex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                    p: 2,
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "#fff",
                                        fontSize: "0.8rem",
                                        letterSpacing: "0.1em",
                                    }}
                                >
                                    {card.category}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: "'Playfair Display', serif",
                                        color: "#fff",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {card.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    onClick={() => handleOpen(card.title, card.content, card.image)}
                                    sx={{
                                        color: "#fff",
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        position: "absolute",
                                        bottom: "45px",
                                        right: "15px",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                        backgroundColor: "rgb(255,255,255,0.2)",
                                        p: 1,
                                    }}
                                >
                                    Discover More &gt;
                                    <Box
                                        sx={{
                                            borderBottom: "2px solid black",
                                            width: "40px",
                                            marginLeft: "5px",
                                        }}
                                    />
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Modal Popup */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogContent
                    sx={{
                        backgroundImage: `
      linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), 
      url(${modalContent.background})
    `,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        color: "#fff",
                        p: 4,
                        // backgroundColor: "rgba(0, 0, 0, 0.1)",
                    }}
                >

                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: "bold",
                            mb: 2,

                        }}
                    >
                        {modalContent.title}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", color: "#fff" }}>
                        {modalContent.content}
                    </Typography>
                </DialogContent>
            </Dialog>


            <Box
                component="hr"
                sx={{
                    border: 0,
                    height: "2px",
                    backgroundColor: "#C5A47E", // Gold color
                    width: "50%", // Width of the line
                    marginTop: "50px", // Center the line
                }}
            />  
        </Box>
    );
}

export default ThrillingTwo;
